import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { API_BASE, authHeaders } from '../constants/api';
import DashboardEditor from './dashboard/DashboardEditor';
import './NewDashboard.css';
import { Suche, Speichern } from '../icons';

/** Leere TB-Konfiguration für ein neues Dashboard */
const EMPTY_CONFIGURATION = {
  widgets: {},
  states: {
    default: {
      name: 'default',
      root: true,
      layouts: {
        main: {
          widgets: {},
          gridSettings: { columns: 24, margin: 10 },
        },
      },
    },
  },
};

function NewDashboard() {
  const navigate = useNavigate();
  const { authToken: token } = useAuth();

  const [dashboardName, setDashboardName] = useState('');
  const [nameError, setNameError] = useState('');
  const nameInputRef = useRef(null);

  const [availableGroups, setAvailableGroups] = useState([]);
  const [groupSearch, setGroupSearch] = useState('');
  const [groupSuggestions, setGroupSuggestions] = useState([]);
  const [showGroupSuggestions, setShowGroupSuggestions] = useState(false);
  const [selectedGroups, setSelectedGroups] = useState([]);
  const [highlightedGroupIdx, setHighlightedGroupIdx] = useState(-1);
  const groupSuggestionsRef = useRef(null);

  const [editorDirty, setEditorDirty] = useState(false);

  /* Warnung beim Schließen des Browser-Tabs */
  useEffect(() => {
    function onBeforeUnload(e) {
      if (editorDirty || dashboardName.trim()) {
        e.preventDefault();
        e.returnValue = '';
      }
    }
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [editorDirty, dashboardName]);

  useEffect(() => {
    if (!token) return;
    async function loadGroups() {
      try {
        const res = await fetch(
          `${API_BASE}/entityGroups/DASHBOARD?pageSize=100&page=0&includeShared=false`,
          { headers: authHeaders(token) }
        );
        if (!res.ok) return;
        const data = await res.json();
        setAvailableGroups((data.data || []).filter(g => g.name !== 'All'));
      } catch {}
    }
    loadGroups();
  }, [token]);


  useEffect(() => {
    const selectedIds = new Set(selectedGroups.map(g => g.id));
    setGroupSuggestions(
      availableGroups
        .filter(
          g =>
            !selectedIds.has(g.id.id) &&
            (!groupSearch.trim() || g.name.toLowerCase().includes(groupSearch.toLowerCase()))
        )
        .slice(0, 8)
    );
  }, [groupSearch, availableGroups, selectedGroups]);

  function addGroup(group) {
    setSelectedGroups(prev => [...prev, { id: group.id.id, name: group.name }]);
    setGroupSearch('');
    setHighlightedGroupIdx(-1);
  }

  function removeGroup(id) {
    setSelectedGroups(prev => prev.filter(g => g.id !== id));
  }

  async function handleCreate(configuration) {
    const name = dashboardName.trim();
    if (!name) {
      setNameError('Bitte geben Sie einen Namen für das Dashboard ein.');
      nameInputRef.current?.focus();
      throw new Error('Kein Dashboard-Name angegeben.');
    }
    setNameError('');

    /* 1. Dashboard erstellen */
    const createRes = await fetch(`${API_BASE}/dashboard`, {
      method: 'POST',
      headers: authHeaders(token),
      body: JSON.stringify({ title: name, configuration }),
    });

    if (!createRes.ok) {
      const errText = await createRes.text().catch(() => '');
      throw new Error(
        `Dashboard konnte nicht erstellt werden (${createRes.status}): ${errText.substring(0, 120)}`
      );
    }

    const created = await createRes.json();
    const newDashboardId = created.id?.id;

    /* 2. Dashboard zu ausgewählten Gruppen hinzufügen */
    for (const group of selectedGroups) {
      try {
        await fetch(`${API_BASE}/entityGroup/${group.id}/addEntities`, {
          method: 'POST',
          headers: authHeaders(token),
          body: JSON.stringify([newDashboardId]),
        });
      } catch (e) {
        console.warn(`Konnte Dashboard nicht zu Gruppe „${group.name}" hinzufügen:`, e);
      }
    }

    /* 3. Weiterleiten zur Dashboard-Ansicht */
    navigate(`/dashboard/view/${newDashboardId}`, {
      state: { name },
    });
  }

  const toolbarExtra = (
    <>
      {/* Name-Eingabe */}
      <div className="nd-field-wrap">
        <label htmlFor="nd-name" className="nd-floating-label">
          Dashboard-Name <span className="nd-required">*</span>
        </label>
        <input
          id="nd-name"
          ref={nameInputRef}
          className={`nd-input${nameError ? ' nd-input--error' : ''}`}
          type="text"
          value={dashboardName}
          placeholder="Name eingeben"
          maxLength={100}
          autoFocus
          autoComplete="off"
          onChange={e => {
            setDashboardName(e.target.value);
            if (nameError && e.target.value.trim()) setNameError('');
          }}
          aria-required="true"
        />
        <span className="nd-required-hint">* Pflichtfeld</span>
        {nameError && (
          <span className="nd-field-error" role="alert">{nameError}</span>
        )}
      </div>

      {/* Gruppen-Suche */}
      <div className="nd-field-wrap nd-field-wrap--group">
        <label htmlFor="nd-groups" className="nd-floating-label">
          Zu Gruppe hinzufügen
        </label>
        <div className="nd-group-row">
          <div className="nd-group-wrap">
            <span className="nd-group-icon"><Suche /></span>
            <input
              id="nd-groups"
              className="nd-input nd-input--group"
              type="text"
              value={groupSearch}
              placeholder="Gruppe suchen…"
              autoComplete="off"
              onChange={e => { setGroupSearch(e.target.value); setHighlightedGroupIdx(-1); }}
              onFocus={() => setShowGroupSuggestions(true)}
              onBlur={() => setTimeout(() => setShowGroupSuggestions(false), 150)}
              onKeyDown={e => {
                if (e.key === 'Escape') { setShowGroupSuggestions(false); setGroupSearch(''); setHighlightedGroupIdx(-1); return; }
                if (!showGroupSuggestions || groupSuggestions.length === 0) return;
                if (e.key === 'ArrowDown') { e.preventDefault(); setHighlightedGroupIdx(p => Math.min(p + 1, groupSuggestions.length - 1)); }
                else if (e.key === 'ArrowUp') { e.preventDefault(); setHighlightedGroupIdx(p => Math.max(p - 1, -1)); }
                else if (e.key === 'Enter' && highlightedGroupIdx >= 0) { e.preventDefault(); addGroup(groupSuggestions[highlightedGroupIdx]); }
              }}
              aria-autocomplete="list"
              aria-controls="nd-group-suggestions"
              aria-activedescendant={
                highlightedGroupIdx >= 0 && showGroupSuggestions && groupSuggestions.length > 0
                  ? `nd-group-suggestion-${highlightedGroupIdx}`
                  : undefined
              }
            />
            {groupSearch && (
              <button type="button" className="nd-group-clear" onClick={() => { setGroupSearch(''); setHighlightedGroupIdx(-1); }} aria-label="Suche zurücksetzen">✖</button>
            )}
            {showGroupSuggestions && groupSuggestions.length > 0 && (
              <div id="nd-group-suggestions" className="nd-group-suggestions" role="listbox" ref={groupSuggestionsRef}>
                {groupSuggestions.map((g, idx) => (
                  <div
                    key={g.id.id}
                    id={`nd-group-suggestion-${idx}`}
                    className={`nd-group-suggestion${highlightedGroupIdx === idx ? ' highlighted' : ''}`}
                    role="option"
                    aria-selected={highlightedGroupIdx === idx}
                    onMouseDown={() => addGroup(g)}
                    onMouseEnter={() => setHighlightedGroupIdx(idx)}
                  >
                    {g.name}
                  </div>
                ))}
              </div>
            )}
            {showGroupSuggestions && groupSuggestions.length === 0 && groupSearch.trim() && (
              <div className="nd-group-suggestions" role="listbox">
                <div className="nd-group-suggestion-no-results">Keine Ergebnisse</div>
              </div>
            )}
          </div>
          {/* Badges rechts neben dem Suchfeld */}
          {selectedGroups.length > 0 && (
            <div className="nd-badges">
              {selectedGroups.map(g => (
                <span key={g.id} className="nd-badge">
                  {g.name}
                  <button type="button" className="nd-badge-remove" onClick={() => removeGroup(g.id)} aria-label={`Gruppe ${g.name} entfernen`}>✖</button>
                </span>
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  );

  return (
    <div className="new-dashboard-page">
      <h1 className="nd-page-title">Neues Dashboard erstellen</h1>
      <DashboardEditor
        configuration={EMPTY_CONFIGURATION}
        dashboardId={null}
        token={token}
        onClose={() => navigate('/dashboards')}
        onDirtyChange={setEditorDirty}
        saveOverride={handleCreate}
        extraSaveDisabled={!dashboardName.trim()}
        saveIcon=<Speichern />
        saveLabel="Dashboard erstellen"
        toolbarExtra={toolbarExtra}
      />
    </div>
  );
}

export default NewDashboard;
